/**
 * Rollen & Permissies Beheer Pagina
 */

import { useState, useEffect } from 'react';
import { rollenService, Rol, Permissie } from '../../services/rollen.service';
import Toast from '../../components/Toast';
import './Settings.css';
import './RollenPermissies.css';

const RollenPermissies = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const [rollen, setRollen] = useState<Rol[]>([]);
  const [permissies, setPermissies] = useState<Permissie[]>([]);
  const [selectedRol, setSelectedRol] = useState<Rol | null>(null);
  const [selectedPermissies, setSelectedPermissies] = useState<Set<number>>(new Set());
  const [standaardPermissies, setStandaardPermissies] = useState<Set<number>>(new Set());

  // Groepeer permissies op categorie
  const permissiesPerCategorie = permissies.reduce((acc, permissie) => {
    const categorie = permissie.categorie || 'Overig';
    if (!acc[categorie]) {
      acc[categorie] = [];
    }
    acc[categorie].push(permissie);
    return acc;
  }, {} as Record<string, Permissie[]>);

  // Sorteer categorieën
  const categorieen = Object.keys(permissiesPerCategorie).sort((a, b) => {
    // Zet 'Instellingen' en 'Klantportaal' onderaan
    if (a === 'Instellingen') return 1;
    if (b === 'Instellingen') return -1;
    if (a === 'Klantportaal') return 1;
    if (b === 'Klantportaal') return -1;
    return a.localeCompare(b);
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rollenData, permissiesData] = await Promise.all([
        rollenService.getRollen(),
        rollenService.getPermissies(),
      ]);
      setRollen(rollenData);
      setPermissies(permissiesData);

      // Selecteer eerste rol (niet Admin)
      const eersteNietAdmin = rollenData.find(r => r.naam !== 'Admin');
      if (eersteNietAdmin) {
        selectRol(eersteNietAdmin);
      }
    } catch (err: any) {
      setError(err.message || 'Fout bij ophalen gegevens');
    } finally {
      setLoading(false);
    }
  };

  const selectRol = async (rol: Rol) => {
    setSelectedRol(rol);
    setSelectedPermissies(new Set(rol.permissies.map(p => p.id)));

    // Laad standaard permissies voor deze rol
    try {
      const standaard = await rollenService.getStandaardPermissies(rol.id);
      setStandaardPermissies(new Set(standaard.permissie_ids));
    } catch {
      setStandaardPermissies(new Set());
    }
  };

  const togglePermissie = (permissieId: number) => {
    if (!selectedRol || selectedRol.naam === 'Admin') return;

    const newSelected = new Set(selectedPermissies);
    if (newSelected.has(permissieId)) {
      newSelected.delete(permissieId);
    } else {
      newSelected.add(permissieId);
    }
    setSelectedPermissies(newSelected);
  };

  const toggleCategorie = (categorie: string) => {
    if (!selectedRol || selectedRol.naam === 'Admin') return;

    const categoriePermissies = permissiesPerCategorie[categorie];
    const alleGeselecteerd = categoriePermissies.every(p => selectedPermissies.has(p.id));

    const newSelected = new Set(selectedPermissies);
    if (alleGeselecteerd) {
      // Deselecteer alle in categorie
      categoriePermissies.forEach(p => newSelected.delete(p.id));
    } else {
      // Selecteer alle in categorie
      categoriePermissies.forEach(p => newSelected.add(p.id));
    }
    setSelectedPermissies(newSelected);
  };

  const handleSave = async () => {
    if (!selectedRol) return;

    try {
      setSaving(true);
      setError(null);
      const updatedRol = await rollenService.updateRolPermissies(
        selectedRol.id,
        Array.from(selectedPermissies)
      );

      // Update lokale state
      setRollen(rollen.map(r => r.id === updatedRol.id ? updatedRol : r));
      setSelectedRol(updatedRol);
      setSuccess(`Permissies voor "${selectedRol.naam}" succesvol opgeslagen!`);
    } catch (err: any) {
      setError(err.message || 'Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    if (!selectedRol) return false;
    const originalIds = new Set(selectedRol.permissies.map(p => p.id));
    if (originalIds.size !== selectedPermissies.size) return true;
    for (const id of selectedPermissies) {
      if (!originalIds.has(id)) return true;
    }
    return false;
  };

  const isStandaard = () => {
    if (!selectedRol || standaardPermissies.size === 0) return true;
    if (standaardPermissies.size !== selectedPermissies.size) return false;
    for (const id of selectedPermissies) {
      if (!standaardPermissies.has(id)) return false;
    }
    return true;
  };

  const handleReset = async () => {
    if (!selectedRol) return;

    try {
      setResetting(true);
      setError(null);
      const updatedRol = await rollenService.resetRolPermissies(selectedRol.id);

      // Update lokale state
      setRollen(rollen.map(r => r.id === updatedRol.id ? updatedRol : r));
      setSelectedRol(updatedRol);
      setSelectedPermissies(new Set(updatedRol.permissies.map(p => p.id)));
      setSuccess(`Permissies voor "${selectedRol.naam}" gereset naar standaard!`);
      setShowResetConfirm(false);
    } catch (err: any) {
      setError(err.message || 'Fout bij resetten');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">Rollen en permissies laden...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Rollen & Permissies</h1>
        <p className="subtitle">Beheer de toegangsrechten per rol</p>
      </div>

      <Toast message={success} type="success" onClose={() => setSuccess(null)} />
      <Toast message={error} type="error" onClose={() => setError(null)} autoClose={0} />

      <div className="rollen-permissies-container">
        {/* Rol selectie tabs */}
        <div className="rollen-tabs">
          {rollen.map(rol => (
            <button
              key={rol.id}
              className={`rol-tab ${selectedRol?.id === rol.id ? 'active' : ''} ${rol.naam === 'Admin' ? 'admin-tab' : ''}`}
              onClick={() => selectRol(rol)}
            >
              <span className="rol-naam">{rol.naam}</span>
              <span className="rol-count">{rol.permissies.length}</span>
            </button>
          ))}
        </div>

        {/* Geselecteerde rol info */}
        {selectedRol && (
          <div className="rol-info">
            <h2>{selectedRol.naam}</h2>
            {selectedRol.omschrijving && (
              <p className="rol-omschrijving">{selectedRol.omschrijving}</p>
            )}
            {selectedRol.naam === 'Admin' && (
              <div className="admin-warning">
                De Admin rol heeft altijd toegang tot alle functionaliteiten en kan niet worden aangepast.
              </div>
            )}
          </div>
        )}

        {/* Permissies grid */}
        {selectedRol && (
          <div className="permissies-grid">
            {categorieen.map(categorie => {
              const categoriePermissies = permissiesPerCategorie[categorie];
              const aantalGeselecteerd = categoriePermissies.filter(p => selectedPermissies.has(p.id)).length;
              const alleGeselecteerd = aantalGeselecteerd === categoriePermissies.length;
              const geenGeselecteerd = aantalGeselecteerd === 0;

              return (
                <div key={categorie} className="permissie-categorie">
                  <div className="categorie-header">
                    <label className="categorie-checkbox">
                      <input
                        type="checkbox"
                        checked={alleGeselecteerd}
                        ref={(el) => {
                          if (el) el.indeterminate = !alleGeselecteerd && !geenGeselecteerd;
                        }}
                        onChange={() => toggleCategorie(categorie)}
                        disabled={selectedRol.naam === 'Admin'}
                      />
                      <span className="categorie-naam">{categorie}</span>
                      <span className="categorie-count">
                        ({aantalGeselecteerd}/{categoriePermissies.length})
                      </span>
                    </label>
                  </div>

                  <div className="permissie-lijst">
                    {categoriePermissies.map(permissie => (
                      <label
                        key={permissie.id}
                        className={`permissie-item ${selectedPermissies.has(permissie.id) ? 'selected' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedPermissies.has(permissie.id)}
                          onChange={() => togglePermissie(permissie.id)}
                          disabled={selectedRol.naam === 'Admin'}
                        />
                        <div className="permissie-info">
                          <span className="permissie-naam">{permissie.omschrijving || permissie.naam}</span>
                          <span className="permissie-code">{permissie.naam}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Save button */}
        {selectedRol && selectedRol.naam !== 'Admin' && (
          <div className="form-actions">
            <button
              type="button"
              className="btn-lg btn-success"
              onClick={handleSave}
              disabled={saving || !hasChanges()}
            >
              {saving ? 'Bezig met opslaan...' : 'Wijzigingen Opslaan'}
            </button>
            <button
              type="button"
              className="btn-lg btn-secondary"
              onClick={() => setShowResetConfirm(true)}
              disabled={resetting || isStandaard()}
              title={isStandaard() ? 'Permissies zijn al standaard' : 'Reset naar standaard permissies'}
            >
              {resetting ? 'Bezig...' : 'Reset naar Standaard'}
            </button>
            {hasChanges() && (
              <span className="unsaved-changes">Er zijn niet-opgeslagen wijzigingen</span>
            )}
          </div>
        )}

        {/* Reset bevestiging modal */}
        {showResetConfirm && selectedRol && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Reset naar Standaard</h3>
              <p>
                Weet je zeker dat je de permissies voor <strong>"{selectedRol.naam}"</strong> wilt
                resetten naar de standaard waarden?
              </p>
              <p className="modal-warning">
                Dit zal alle aangepaste permissies overschrijven.
              </p>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowResetConfirm(false)}
                  disabled={resetting}
                >
                  Annuleren
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleReset}
                  disabled={resetting}
                >
                  {resetting ? 'Bezig...' : 'Ja, Reset'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RollenPermissies;
